import { Component, OnInit, Input, ChangeDetectorRef, Output, EventEmitter } from "@angular/core";
import { DateTime } from "luxon";
import { Helper } from "src/app/core/helpers/helper";
import { FlatpickrDefaultsInterface } from "angularx-flatpickr";
import { english } from "flatpickr/dist/l10n/default";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import { DatePickerTabs, FlatePickrMode } from "src/app/core/constants/constants";

const customLocale = {
  ...english,
  firstDayOfWeek: 1
};

@Component({
  selector: "app-date-picker",
  templateUrl: "./date-picker.component.html",
  styleUrls: ["./date-picker.component.scss"],
  standalone: false
})
export class DatePickerComponent implements OnInit {
  @Input() title: string | undefined;
  @Input() datePickerRef: any; // Replace with appropriate type
  @Input() isScheduleDate!: boolean;
  @Output() dateRangeChange = new EventEmitter<any>();

  groupBy: { label: string; value: number; mode: FlatePickrMode }[] = [
    { label: this.helper.translateService.instant("label-title.day"), value: DatePickerTabs.day, mode: FlatePickrMode.single },
    { label: this.helper.translateService.instant("label-title.week"), value: DatePickerTabs.week, mode: FlatePickrMode.range },
    { label: this.helper.translateService.instant("label-title.month"), value: DatePickerTabs.month, mode: FlatePickrMode.single },
    { label: this.helper.translateService.instant("label-title.range"), value: DatePickerTabs.range, mode: FlatePickrMode.range }
  ];

  todayDate: Date = new Date();
  scheduleMindate: Date = new Date();

  date: any;
  dateRange: any;
  @Input() filterDate: any;

  @Input() activeTab: number = DatePickerTabs.day;

  datePickerOptions: FlatpickrDefaultsInterface = {
    plugins: [],
    locale: customLocale
  };

  monthPickerOptions: FlatpickrDefaultsInterface = {
    plugins: [
      monthSelectPlugin({
        shorthand: true,
        dateFormat: "F Y",
        altFormat: "F Y",
        theme: "material_blue"
      })
    ]
  };

  constructor(public helper: Helper, private cdr: ChangeDetectorRef) {}

  /**
   * Initializes the component. Sets default values if filterDate is not provided.
   */
  ngOnInit(): void {
    if (!this.filterDate.from) {
      this.onTabChange(this.activeTab);
    } else {
      this.setDefaults();
    }
  }

  /**
   * Handles tab change events. Sets the appropriate date range based on the active tab.
   *
   * @param activeTab The currently active tab index.
   */
  onTabChange(activeTab: number): void {
    const today = DateTime.fromJSDate(new Date());

    switch (activeTab) {
      case 1:
        this.scheduleMindate = new Date();
        this.date = today.startOf("day").toJSDate();
        this.filterDate = { from: today.startOf("day").toJSDate(), to: today.endOf("day").toJSDate() };
        break;
      case 2:
        this.scheduleMindate = DateTime.fromJSDate(new Date()).startOf("week").startOf("day").toJSDate();
        this.dateRange = { from: today.startOf("week").toJSDate(), to: today.endOf("week").startOf("day").toJSDate() };
        this.filterDate = { from: today.startOf("week").toJSDate(), to: today.endOf("week").toJSDate() };
        break;
      case 3:
        this.date = today.startOf("month").startOf("day").toJSDate();
        this.filterDate = { from: today.startOf("month").startOf("day").toJSDate(), to: today.endOf("month").endOf("day").toJSDate() };
        this.selectCurrentMonth();
        break;
      case 4:
        this.scheduleMindate = new Date();
        this.dateRange = { from: today.minus({ days: 6 }).startOf("day").toJSDate(), to: today.startOf("day").toJSDate() };
        this.filterDate = { from: today.minus({ days: 6 }).startOf("day").toJSDate(), to: today.endOf("day").toJSDate() };
        break;
      default:
        break;
    }

    this.activeTab = activeTab;
  }

  /**
   * Gets the minimum date for the month schedule.
   *
   * @returns The minimum date for the month schedule.
   */
  getMonthScheduleMinDate(): any {
    return DateTime.fromJSDate(new Date()).minus({ months: 1 }).endOf("month").toJSDate();
  }

  /**
   * Sets default values based on the active tab and filterDate.
   */
  setDefaults(): void {
    switch (this.activeTab) {
      case 1:
      case 3:
        this.date = new Date(this.filterDate.from);
        if (this.isScheduleDate) {
          this.date = new Date();
        }
        if (this.date) {
          this.selectCurrentMonth();
        }
        break;
      case 2:
      case 4:
        this.scheduleMindate = DateTime.fromJSDate(new Date()).startOf("week").startOf("day").toJSDate();
        this.filterDate.from = new Date(this.filterDate.from);
        this.filterDate.to = new Date(this.filterDate.to);
        this.dateRange = { ...this.filterDate };
        break;
      default:
        break;
    }
  }

  /**
   * Handles date change events from the date picker.
   *
   * @param event The event object containing the selected dates.
   */
  onDateChange(event: any): void {
    const selectedDates = event.selectedDates;

    switch (this.activeTab) {
      case 1:
        this.date = DateTime.fromJSDate(selectedDates[0]).startOf("day").toJSDate();
        this.filterDate = { from: DateTime.fromJSDate(selectedDates[0]).startOf("day").toJSDate(), to: DateTime.fromJSDate(selectedDates[0]).endOf("day").toJSDate() };
        break;
      case 2:
        this.filterDate = {
          from: DateTime.fromJSDate(selectedDates[0]).startOf("week").startOf("day").toJSDate(),
          to: DateTime.fromJSDate(selectedDates[0]).endOf("week").endOf("day").toJSDate()
        };
        this.dateRange = { ...this.filterDate };
        break;
      case 3:
        this.filterDate = {
          from: DateTime.fromJSDate(selectedDates[0]).startOf("month").startOf("day").toJSDate(),
          to: DateTime.fromJSDate(selectedDates[0]).endOf("month").endOf("day").toJSDate()
        };
        this.date = DateTime.fromJSDate(selectedDates[0]).startOf("month").startOf("day").toJSDate();
        break;

      case 4:
        this.filterDate = { from: DateTime.fromJSDate(selectedDates[0]).startOf("day").toJSDate(), to: DateTime.fromJSDate(selectedDates[1]).endOf("day").toJSDate() };
        this.dateRange = { ...this.filterDate };
        break;

      default:
        break;
    }
    this.cdr.detectChanges();
  }

  /**
   * Gets the selected date or date range based on the active tab.
   *
   * @returns The selected date or date range.
   */
  get selectedDate(): any {
    return [1, 3].includes(this.activeTab) ? this.date : this.dateRange;
  }

  /**
   * Sets the selected date or date range based on the active tab.
   *
   * @param value The date or date range to set.
   */
  set selectedDate(value) {
    if ([1, 3].includes(this.activeTab)) {
      this.date = value;
    } else {
      this.dateRange = value;
    }
  }

  /**
   * Selects the current month in the month picker.
   */
  selectCurrentMonth(): void {
    const targetMonth = new Date(this.date).getMonth();
    if (targetMonth) {
      setTimeout(() => {
        const month = document.querySelector(`.flatpickr-monthSelect-month:nth-child(${targetMonth + 1})`);
        if (month) {
          month.classList.add("selected");
        }
      }, 100);
    }
  }

  /**
   * Confirms the selected date or date range and emits the date range change event.
   */
  confirmDate(): void {
    this.emitDateRange();
  }

  /**
   * Closes the date picker and emits the date range change event with a false value.
   */
  colseDate(): void {
    this.dateRangeChange.emit(false);
  }

  /**
   * Emits the date range change event with the formatted date and filterDate.
   */
  emitDateRange(): void {
    let formattedDate = "";
    const toLabel = this.helper.translateService.instant("label-title.to");

    // Define a function to check if a date is valid
    const isValidDate = (date: Date | null): boolean => {
      return date instanceof Date && !isNaN(date.getTime());
    };

    switch (this.activeTab) {
      case 1:
        if (!isValidDate(this.date)) {
          return;
        }
        formattedDate = DateTime.fromJSDate(this.date).toFormat("dd-MM-yyyy");
        break;
      case 2:
      case 4:
        if (!isValidDate(this.filterDate.from) || !isValidDate(this.filterDate.to)) {
          return;
        }
        formattedDate = `${DateTime.fromJSDate(this.filterDate.from).toFormat("dd-MM-yyyy")} ${toLabel} ${DateTime.fromJSDate(this.filterDate.to).toFormat("dd-MM-yyyy")}`;
        break;
      case 3:
        if (!isValidDate(this.date)) {
          return;
        }
        formattedDate = `${DateTime.fromJSDate(this.date).toFormat("MMM yyyy")}`;
        break;
      default:
        break;
    }

    if (this.filterDate.from && this.filterDate.to) {
      if (!isValidDate(this.filterDate.from) || !isValidDate(this.filterDate.to)) {
        return;
      }

      // convert date to format MM-dd-yyyy
      this.filterDate.from = DateTime.fromJSDate(this.filterDate.from).toFormat("MM-dd-yyyy");
      this.filterDate.to = DateTime.fromJSDate(this.filterDate.to).toFormat("MM-dd-yyyy");

      formattedDate = `${DateTime.fromFormat(this.filterDate.from, "MM-dd-yyyy").toFormat("dd-MM-yyyy")} ${toLabel} ${DateTime.fromFormat(
        this.filterDate.to,
        "MM-dd-yyyy"
      ).toFormat("dd-MM-yyyy")}`;
    }

    if (formattedDate) {
      this.dateRangeChange.emit({ formattedDate, filterDate: this.filterDate, activeTab: this.activeTab });
    }
  }
}
